import React, { useEffect, useState } from 'react';
import StickyHeader from '@hpstellar/pdp/StickyHeader/StickyHeader.js';

import AddOnPrice from '../../../product/components/product-configurations/addon-price';
import withPageComponents from '../../../page/components/with-page-components';
import { PriceContainer } from '../../../product/components/product-price/with-product-price';
import IntraDayDeals from '../../../product/components/deals';
import { Helpers } from '../../../core/src/helpers';
import { withAddToCart } from '../../../product/components/purchase-button-container';

const MIN_HEIGHT = 58;
const ACC_TYPES = {
    Toner: ['samsung', 'laserjet'],
    Ink: ['deskjet', 'envy', 'officejet', 'pagewide', 'designjet', 'tango']
}

const getProductObj = (product, price, dealProps) => {
    const { isActive, productDeals } = dealProps;
    const deal = isActive ? productDeals.activeDeal : null;
    const { name, prdClass, isComingSoon } = product;
    let { regularPrice, salePrice, ctaText, customizeText, ctoLink, isOOS } = (price || {})
    const prodObj = {
        name,
        price: {
            currencySymbol: '$',
            productType: prdClass && prdClass.toLowerCase(),
            regularPrice: regularPrice !== "0" ? regularPrice : undefined,
            salePrice: deal ? deal.dealPrice * 1 : salePrice !== '0' ? salePrice : undefined
        }
    }
    return {
        product: prodObj,
        cta: {
            ctaText: isComingSoon ? 'Coming Soon' : ctaText,
            ctoLink,
            customizeText,
            addBtnDisabled: !isComingSoon ? isOOS : false
        }
    };
}

const FEATURE_LINK = {
    title: 'Features',
    hash: '@hpstellar/pdp/features'
}

const getAccType = (product) => {
    try {
        const productName = (product.name || "").toLowerCase();
        let category = (product.attributes || {}).pm_category;
        if (Array.isArray(category)) {
            category = category[0];
        }
        if (category && category.toLowerCase().includes('printer')) {
            for (let accType of Object.keys(ACC_TYPES)) {
                if (ACC_TYPES[accType].some(printer => productName.includes(printer))) {
                    return accType;
                }
            }
        }
        return null;
    } catch (e) {
        return null;
    }
}

const getAnchorLinks = (anchorLinks = [], pdpCrossSell, pdpUpsell, exclusiveFeatures, product) => {
    let completeNavLink = anchorLinks.reduce((r, link, i) => {
        //add features in 2nd position if available
        if (i === 1 && exclusiveFeatures && link.title !== FEATURE_LINK.title) {
            r.push(FEATURE_LINK)
        }
        r.push(link);
        return r;
    }, []);
    if (pdpCrossSell && pdpCrossSell.relatedProducts && pdpCrossSell.relatedProducts.length > 0) {
        const accType = getAccType(product);
        let title = accType ? `${accType} & Paper` : 'Recommended accessories';
        completeNavLink.push(
            {
                title: title,
                hash: 'recommendedAccessories'
            })
    }
    if (pdpUpsell && pdpUpsell.productsList && pdpUpsell.productsList.length > 0) {
        completeNavLink.push(
            {
                title: 'Similar products',
                hash: 'similarProducts'
            })
    }
    return completeNavLink;
}

const stickyNavOnClick = (product, event, trackCustomMetric) => {
    try {
        let tabName = event.target.getAttribute('data-text');

        const TABS_ANALYTICS_MAP = {
            'Specs': 'prodDetailsTab',
            'Features': 'prodDetailsTab',
            'Ink & Paper': 'prodDetailsTab',
            'Accessories': 'prodDetailsTab',
            'Support': 'prodDetailsTab',
            'Reviews': 'readReview',

            // v2
            'Overview': 'prodDetailsTab',
            'Technical details': 'prodDetailsTab',
            'Special Offers': 'prodDetailsTab',
            'Similar products': 'prodDetailsTab',
            'Recommended accessories': 'prodDetailsTab'
        }
        let eventName = TABS_ANALYTICS_MAP[tabName];

        if (eventName && product && product.name) {
            trackCustomMetric(
                eventName,
                {
                    event: `e_${eventName}`,
                    productName: product.name,
                    detailsTabName: tabName,
                }
            )
        }
    } catch (e) { }
}

const StickyNAV = ({ anchorLinks, product, productData, onAdd, cta, trackCustomMetric }) => {
    const [isSticky, setIsSticky] = useState(false);
    const [activeLink, setActiveLink] = useState('');

    let lastKnownScrollPosition = 0;
    let ticking = false;
    let [firstSection] = anchorLinks;
    const showStickyHeader = () => {
        //add min height of the sticky nav so it treats the bottom of the nav bar as the position
        //currently upperBar is visible at the same time as the nav become stick, so double the height
        lastKnownScrollPosition = window.scrollY + (MIN_HEIGHT * 2);
        if (!ticking) {
            window.requestAnimationFrame(() => {
                try {
                    let firstSectionElem = document.getElementById(firstSection.hash)
                    if (firstSectionElem && lastKnownScrollPosition > firstSectionElem.offsetTop) {
                        setIsSticky(true);
                    } else {
                        setIsSticky(false);
                    }
                    let activeHash;
                    anchorLinks.forEach(({ hash }) => {
                        let anchorElem = document.getElementById(hash)
                        if (anchorElem && lastKnownScrollPosition > anchorElem.offsetTop) {
                            activeHash = hash;
                        }
                    });
                    setActiveLink(activeHash);

                    ticking = false;
                } catch (e) { }
            });
            ticking = true;
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', showStickyHeader);
        return () => {
            window.removeEventListener('scroll', showStickyHeader);
        };
    }, [anchorLinks]);
    /**
     * Temp wrapper div to reserve space for SSR
     */
    return (<div style={{ minHeight: `${MIN_HEIGHT}px` }} onClick={value => stickyNavOnClick(productData, value, trackCustomMetric)}><StickyHeader
        product={product}
        anchorLinks={anchorLinks}
        sticky={isSticky}
        upperBarVisible={isSticky}
        activeLink={activeLink}
        onAdd={onAdd}
        addText={cta.ctaText}
        customizeText={cta.customizeText || 'Customize & Buy'}
        customizeUrl={cta.ctoLink}
        addBtnDisabled={cta.addBtnDisabled}
    /></div>);
};

const navContainer = ({
    product,
    pdpStickyNAV,
    pdpCrossSell,
    pdpUpsell,
    exclusiveFeatures,
    addToCart,
    addCTOToCart,
    device,
    trackCustomMetric
}) => {

    if (product && pdpStickyNAV && device !== 'mobile') {
        const { anchorLinks: initialLinks } = pdpStickyNAV;
        let anchorLinks = getAnchorLinks(initialLinks, pdpCrossSell, pdpUpsell, exclusiveFeatures, product);
        return (anchorLinks.length > 0 &&
            <IntraDayDeals itemId={product.itemId}>
                {dealProps =>
                (<PriceContainer products={[product]}>
                    {
                        ({ prices }) => {
                            const productPrice = prices[product.sku];
                            return (
                                <AddOnPrice sku={product.sku} price={productPrice}>
                                    {
                                        ({ totalPrice }) => {
                                            const { product: productObj, cta } = getProductObj(product, totalPrice, dealProps);
                                            return (<StickyNAV
                                                product={productObj}
                                                productData={product}
                                                anchorLinks={anchorLinks}
                                                onAdd={() => {
                                                    if (product.prdClass === 'CTO') {
                                                        addCTOToCart(product, 1)
                                                    } else {
                                                        //TODO: temporary until Xsell refactor is complete
                                                        let mainBtn = document.getElementById("pdpAddToCartBtn")
                                                        if (mainBtn) {
                                                            mainBtn.click();
                                                        } else {
                                                            addToCart(product, 1);
                                                        }
                                                    }
                                                }}
                                                cta={cta}
                                                trackCustomMetric={trackCustomMetric}
                                            />)
                                        }
                                    }
                                </AddOnPrice>);
                        }
                    }
                </PriceContainer>)
                }
            </IntraDayDeals>);
    }
    return null;
}


export default withAddToCart(withPageComponents(navContainer, { components: ['product', 'pdpStickyNAV', 'pdpUpsell', 'pdpCrossSell', 'exclusiveFeatures'], device: true }))